import pb, { requireAuth } from '../pocketbaseClient';

class CampaignService {
    constructor() {
        this.pb = pb;
    }

    // Authentication methods
    async authenticateAdmin(username, password) {
        try {
            const authData = await this.pb.admins.authWithPassword(username, password);
            return { success: true, data: authData };
        } catch (error) {
            console.error('Admin authentication failed:', error);
            return { success: false, error: error.message };
        }
    }

    async authenticateUser(email, password) {
        try {
            const authData = await this.pb.collection('users').authWithPassword(email, password);
            return { success: true, data: authData };
        } catch (error) {
            console.error('User authentication failed:', error);
            return { success: false, error: error.message };
        }
    }

    async logout() {
        this.pb.authStore.clear();
    }

    // Campaign CRUD operations
    async getCampaigns() {
        try {
            requireAuth();
            const records = await this.pb.collection('campaigns').getFullList({
                sort: '-created'
            });
            return { success: true, data: records };
        } catch (error) {
            // Handle auto-cancellation gracefully
            if (error.message && error.message.includes('autocancelled')) {
                console.log('Request was auto-cancelled, ignoring...');
                return { success: true, data: [] };
            }
            console.error('Error fetching campaigns:', error);
            return { success: false, error: error.message };
        }
    }

    async getCampaignById(id) {
        try {
            const record = await this.pb.collection('campaigns').getOne(id);
            return { success: true, data: record };
        } catch (error) {
            // Handle auto-cancellation gracefully
            if (error.message && error.message.includes('autocancelled')) {
                console.log('Request was auto-cancelled, ignoring...');
                return { success: false, error: 'Request cancelled' };
            }
            console.error('Error fetching campaign:', error);
            return { success: false, error: error.message };
        }
    }

    // Helper function to convert names to URL-friendly format
    toUrlFriendly(name) {
        return name
            .toLowerCase()
            .replace(/\s+/g, '-')  // Replace spaces with hyphens
            .replace(/[^a-z0-9-]/g, '')  // Remove any other special characters
            .replace(/-+/g, '-')  // Replace multiple hyphens with single hyphen
            .replace(/^-|-$/g, '');  // Remove leading/trailing hyphens
    }

    async getCampaignByRoute(companyName, campaignName, landingPageName) {
        try {
            // Decode URL-encoded parameters to handle whitespaces and special characters
            const decodedCompanyName = decodeURIComponent(companyName);
            const decodedCampaignName = decodeURIComponent(campaignName);
            const decodedLandingPageName = decodeURIComponent(landingPageName);
            
            console.log('Searching for campaign with route:', {
                companyName: decodedCompanyName,
                campaignName: decodedCampaignName,
                landingPageName: decodedLandingPageName
            });
            
            // Look for campaign with exact route match
            const record = await this.pb.collection('campaigns').getFirstListItem(
                `company_name="${decodedCompanyName}" && campaign_name="${decodedCampaignName}" && landing_page_name="${decodedLandingPageName}"`
            );
            
            console.log('Found campaign via route match:', record);
            return { success: true, data: record };
        } catch (error) {
            // Handle auto-cancellation gracefully
            if (error.message && error.message.includes('autocancelled')) {
                console.log('Request was auto-cancelled, ignoring...');
                return { success: false, error: 'Request cancelled' };
            }
            console.error('Error fetching campaign by route:', error);
            return { success: false, error: error.message };
        }
    }

    async createCampaign(campaignData) {
        try {
            requireAuth();
            const record = await this.pb.collection('campaigns').create(campaignData);
            return { success: true, data: record };
        } catch (error) {
            console.error('Error creating campaign:', error);
            return { success: false, error: error.message };
        }
    }

    async updateCampaign(id, campaignData) {
        try {
            requireAuth();
            const record = await this.pb.collection('campaigns').update(id, campaignData);
            return { success: true, data: record };
        } catch (error) {
            console.error('Error updating campaign:', error);
            return { success: false, error: error.message };
        }
    }

    async deleteCampaign(id) {
        try {
            requireAuth();
            await this.pb.collection('campaigns').delete(id);
            return { success: true };
        } catch (error) {
            console.error('Error deleting campaign:', error);
            return { success: false, error: error.message };
        }
    }

    // Applicant operations
    async addApplicantToCampaign(campaignId, applicantData) {
        try {
            requireAuth();
            // Get current campaign
            const campaign = await this.pb.collection('campaigns').getOne(campaignId);
            
            // Add new applicant to the applicants array
            const updatedApplicants = [...(campaign.applicants || []), applicantData];
            
            // Update campaign with new applicant
            const record = await this.pb.collection('campaigns').update(campaignId, {
                applicants: updatedApplicants
            });
            
            return { success: true, data: record };
        } catch (error) {
            console.error('Error adding applicant to campaign:', error);
            return { success: false, error: error.message };
        }
    }

    async updateApplicantInCampaign(campaignId, leadID, applicantData) {
        try {
            requireAuth();
            // Get current campaign
            const campaign = await this.pb.collection('campaigns').getOne(campaignId);
            
            // Update specific applicant in the array
            const updatedApplicants = (campaign.applicants || []).map(applicant => 
                applicant.leadID === leadID ? { ...applicant, ...applicantData } : applicant
            );
            
            // Update campaign
            const record = await this.pb.collection('campaigns').update(campaignId, {
                applicants: updatedApplicants
            });
            
            return { success: true, data: record };
        } catch (error) {
            console.error('Error updating applicant in campaign:', error);
            return { success: false, error: error.message };
        }
    }

    async deleteApplicantFromCampaign(campaignId, leadID) {
        try {
            requireAuth();
            // Get current campaign
            const campaign = await this.pb.collection('campaigns').getOne(campaignId);
            
            // Remove applicant from the array
            const updatedApplicants = (campaign.applicants || []).filter(
                applicant => applicant.leadID !== leadID
            );
            
            // Update campaign
            const record = await this.pb.collection('campaigns').update(campaignId, {
                applicants: updatedApplicants
            });
            
            return { success: true, data: record };
        } catch (error) {
            console.error('Error deleting applicant from campaign:', error);
            return { success: false, error: error.message };
        }
    }

    // Landing page operations
    async updateCampaignLandingPage(campaignId, landingPageData) {
        try {
            requireAuth();
            const record = await this.pb.collection('campaigns').update(campaignId, {
                landing_page_data: landingPageData
            });
            return { success: true, data: record };
        } catch (error) {
            console.error('Error updating landing page:', error);
            return { success: false, error: error.message };
        }
    }

    // Questionnaire operations
    async updateCampaignQuestionnaire(campaignId, questionnaireData) {
        try {
            requireAuth();
            const record = await this.pb.collection('campaigns').update(campaignId, {
                questionnaire_data: questionnaireData
            });
            return { success: true, data: record };
        } catch (error) {
            console.error('Error updating questionnaire:', error);
            return { success: false, error: error.message };
        }
    }

    // Statistics operations
    async updateCampaignStats(campaignId, statsData) {
        try {
            requireAuth();
            const record = await this.pb.collection('campaigns').update(campaignId, {
                stats: statsData
            });
            return { success: true, data: record };
        } catch (error) {
            console.error('Error updating campaign stats:', error);
            return { success: false, error: error.message };
        }
    }

    // Utility methods
    isAuthenticated() {
        return this.pb.authStore.isValid;
    }

    getCurrentUser() {
        return this.pb.authStore.model;
    }

    // Real-time subscriptions (for future use)
    subscribeToCampaigns(callback) {
        return this.pb.collection('campaigns').subscribe('*', callback);
    }

    subscribeToCampaign(campaignId, callback) {
        return this.pb.collection('campaigns').subscribe(campaignId, callback);
    }
}

// Create and export singleton instance
const campaignService = new CampaignService();
export default campaignService;
